'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const cards = [
  {
    tag: 'Expand',
    href: '/expand',
    title: 'Extend your sales team',
    body: 'Your closers are busy closing. Add dedicated outbound capacity without adding headcount.',
  },
  {
    tag: 'Replace',
    href: '/replace-ai-sdr',
    title: 'Your AI SDR did not work out',
    body: 'You bought the demo. The replies never came. Same budget line, human conversations.',
  },
  {
    tag: 'Scale',
    href: '/just-raised-a-b',
    title: 'You just raised A or B',
    body: 'The board expects pipeline this quarter, not after a six-month hiring cycle.',
  },
  {
    tag: 'Start',
    href: '/first-sdr-function',
    title: 'Your first SDR function',
    body: 'Your deal size cannot support a $100K hire. It can support this.',
  },
  {
    tag: 'Switch',
    href: '/switching-providers',
    title: 'Your provider got acquired',
    body: 'New pod, new account manager, worse results. Switching is a 30-day decision.',
  },
];

export default function SolutionsGrid() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const els = sectionRef?.current?.querySelectorAll('.rev');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 bg-white">
      <div className="max-w-site mx-auto px-7">
        <div className="rev mb-13 max-w-[780px]">
          <div className="eyebrow">Built for where you are</div>
          <h2 className="heading-2 text-ink">Five situations we solve</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards?.map((card, i) => (
            <Link
              key={card?.href}
              href={card?.href}
              className="rev block bg-white border border-line-color rounded-brand p-7 transition-all duration-200 hover:-translate-y-1 hover:shadow-card group"
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              <span className="block font-mono text-[11px] tracking-[0.18em] text-primary uppercase mb-3.5">
                {card?.tag}
              </span>
              <h3 className="heading-3 text-ink mb-3">{card?.title}</h3>
              <p className="text-[15px] text-muted-text leading-relaxed">{card?.body}</p>
              <span className="block font-mono text-[12.5px] tracking-[0.12em] text-primary uppercase mt-4 group-hover:underline">
                Read more →
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}